import { useEffect, useState, type ReactNode } from "react";

import { cx } from "./cx";

/**
 * Skeletons: tier 2 of the loading system (`docs/UX-LOADING.md`, #1043).
 *
 * Grey blocks in the shape of what is coming, for a short load whose layout is known. Every
 * skeleton obeys the same seven rules, and the timing ones live here so no screen restates
 * them:
 *
 * 1. Nothing is drawn for the first `SKELETON_SHOW_DELAY_MS` of a load, but the space the
 *    skeleton will take is reserved from the first frame (#1049): the blocks are laid out,
 *    unpainted, so nothing on the page moves when they appear. An answer inside the delay
 *    is a silent swap to the content in place: no skeleton frame, no fade, no jump.
 * 2. Once drawn, a skeleton stays at least `SKELETON_MIN_SHOW_MS`, so a load that lands just
 *    after it appeared does not blink it.
 * 3. It leaves by a `SKELETON_FADE_MS` crossfade, never an instant swap. With reduced motion
 *    the swap is instant.
 * 4. The blocks are static `surface-2`. No shimmer: the design is a calm instrument, and the
 *    only pulses left are ones that report an event.
 * 5. One `aria-busy` region and one announcement per group, the state's own loading line
 *    (`sessions.loading`, `billing.loading`, ...); the blocks are hidden from the a11y tree.
 * 6. A failure is the screen's error state (a plain line, and Retry where it has one); the
 *    skeleton leaves exactly as it does for content.
 * 7. Content whose layout cannot be known in advance (the plan card family) gets one neutral
 *    card-sized block (`SkeletonCard`), then the real card fades in.
 */

/** Rule 1: how long a load runs before any skeleton is drawn. */
export const SKELETON_SHOW_DELAY_MS = 400;
/** Rule 2: the least time a drawn skeleton stays. */
export const SKELETON_MIN_SHOW_MS = 300;
/**
 * Rule 3: the crossfade out, in `screen-in`'s rhythm. The `skeleton-in` and `skeleton-out`
 * animations in `styles/tailwind.css` run this long; change one and change the other.
 */
export const SKELETON_FADE_MS = 200;

/**
 * Where a load stands, as the screen draws it:
 *
 * - `waiting`: loading, inside the show delay. The blocks hold their space, unpainted.
 * - `skeleton`: the blocks, with the announcement.
 * - `leaving`: the content (or the error) fading in over the blocks fading out.
 * - `content`: settled. Also where a fast load goes straight from `waiting`.
 */
export type RevealPhase = "waiting" | "skeleton" | "leaving" | "content";

type Reveal = { stage: "idle" } | { stage: "shown" | "leaving"; at: number };

const IDLE: Reveal = { stage: "idle" };

/**
 * The timing controller behind every skeleton (rules 1-3). `loading` is the screen's own
 * answer to "is this still being fetched"; a failure is `loading: false` like a success, and
 * leaves the same way.
 *
 * Every state change happens in a timer callback, never during render or synchronously in
 * an effect, and every timer is cleared by its effect, so a remount (development runs each
 * effect twice) cannot leave one behind.
 */
export function useDelayedReveal(loading: boolean): RevealPhase {
  const [reveal, setReveal] = useState<Reveal>(IDLE);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (reveal.stage === "idle") {
      if (loading) timer = setTimeout(() => setReveal({ stage: "shown", at: Date.now() }), SKELETON_SHOW_DELAY_MS);
    } else if (reveal.stage === "shown") {
      if (!loading) {
        const left = Math.max(0, reveal.at + SKELETON_MIN_SHOW_MS - Date.now());
        timer = setTimeout(() => setReveal({ stage: "leaving", at: reveal.at }), left);
      }
    } else if (loading) {
      // Loading again mid-fade (a Retry): back to the blocks, which are still drawn.
      timer = setTimeout(() => setReveal({ stage: "shown", at: Date.now() }), 0);
    } else {
      timer = setTimeout(() => setReveal(IDLE), SKELETON_FADE_MS);
    }
    return () => clearTimeout(timer);
  }, [loading, reveal]);

  if (reveal.stage === "idle") return loading ? "waiting" : "content";
  if (reveal.stage === "shown" || loading) return "skeleton";
  return "leaving";
}

/**
 * One skeleton group: the region, its announcement, and the swap to `children`.
 *
 * `children` is what the screen shows once the load has settled, success or failure; it is
 * not rendered while loading. Settled, both wrappers are `display: contents`, so the content
 * sits in the screen's own flow exactly as it did before this gate existed.
 *
 * While loading, the region is a plain block and the blocks sit in it in flow, so their
 * edge margins collapse with the screen's as the content's will (#1049). The blocks are one
 * node from the first frame to the end of the fade: `invisible` while `waiting` (laid out,
 * not painted; `data-skeleton-reserve` only), then painted (`data-skeleton` too) without a
 * remount or a change of box. For the crossfade the content takes their place in flow and
 * the blocks lie over it, from the region's top, fading out.
 *
 * `live` is false when the gate sits inside a host that is already a live region (the notes
 * card): the announcement is then plain hidden text for the host to read, not a second
 * `role="status"`.
 */
export function SkeletonGate({
  loading,
  label,
  skeleton,
  children,
  live = true,
  className,
}: {
  loading: boolean;
  /** The state's own loading line, announced once when the blocks appear. */
  label: string;
  /** The blocks, in the shape of what is coming. */
  skeleton: ReactNode;
  children?: ReactNode;
  live?: boolean;
  /** Layout for the region while it is loading or fading. */
  className?: string;
}) {
  const phase = useDelayedReveal(loading);
  const held = phase !== "content";
  return (
    <div
      data-skeleton-phase={phase}
      aria-busy={phase === "waiting" || phase === "skeleton" ? true : undefined}
      className={held ? cx("relative", className) : "contents"}
    >
      {held ? (
        <div
          data-skeleton-reserve
          data-skeleton={phase === "waiting" ? undefined : ""}
          aria-hidden="true"
          className={cx(
            phase === "waiting" && "invisible",
            // Out of flow at the content, from the region's top, and never in the way of a
            // tap on it. The first block's top margin had collapsed into the region's; here
            // it would push the blocks down.
            phase === "leaving" &&
              "absolute inset-x-0 top-0 pointer-events-none [&>:first-child]:mt-0 motion-safe:animate-skeleton-out motion-reduce:hidden"
          )}
        >
          {skeleton}
        </div>
      ) : null}
      {phase === "skeleton" ? (
        <span className="sr-only" role={live ? "status" : undefined}>
          {label}
        </span>
      ) : null}
      {phase === "leaving" || phase === "content" ? (
        // `relative` while fading: positioned after the blocks, the content paints above them.
        <div className={phase === "leaving" ? "relative motion-safe:animate-skeleton-in" : "contents"}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** A static block. Size and shape come from the caller, as token classes. */
export function Skeleton({ className }: { className?: string }) {
  return <span className={cx("block bg-surface-2", className)} />;
}

/**
 * The text sizes a line of skeleton stands in for. The line box is the real text's (its
 * size and line height), so the blocks take the height the words will; the bar inside is
 * about the cap height, centred.
 */
const LINE = {
  person: "text-person",
  row: "text-person leading-row",
  note: "text-note",
  label: "text-label",
  hint: "text-hint",
  count: "text-count",
  link: "text-link",
} as const;

/**
 * One line of text, as a bar. `width` is a token or fraction class (`w-3/5`). The inner
 * box is one line height tall (`h-lh`): with no words in it, a line box would otherwise
 * shrink to the bar. `className` lands on the outer box, so padding (a link's `p-link`)
 * adds to the line as it does on the real element.
 */
export function SkeletonLine({
  size = "note",
  width = "w-3/5",
  className,
}: {
  size?: keyof typeof LINE;
  width?: string;
  className?: string;
}) {
  return (
    <span className={cx("block", LINE[size], className)}>
      <span className="flex items-center h-lh">
        <Skeleton className={cx("h-[0.75em] rounded-pill", width)} />
      </span>
    </span>
  );
}

/**
 * Rule 7: one neutral block the size of a card, for content whose layout is not known
 * until it arrives. The common plan card's height (a current plan, 225 px at phone width;
 * 224 here, from the spacing scale), not a guess at a layout (#1049).
 */
export function SkeletonCard({ className }: { className?: string }) {
  return <Skeleton className={cx("w-full h-56 rounded-card", className)} />;
}
